import imageCompression from "browser-image-compression"
import { faCloudUpload } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Typography } from "@mui/material"
import theme from "css/theme/light"
import { useEffect, useMemo, useState } from "react"
import { DropzoneState, useDropzone } from "react-dropzone"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { IFormUploadFileProps } from "./formuploadfile.interface"
import { useTranslation } from "react-i18next"

interface DropzoneFile extends File {
  path?: string
  preview?: string
}

const baseStyle = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  padding: "20px",
  color: theme.palette.primary.dark,
  outline: "none",
  transition: "background .24s ease-in-out",
  cursor: "pointer",
  margin: "0 15px",
  borderRadius: "4px"
}

const activeStyle = { backgroundColor: "white" }
const acceptStyle = { backgroundColor: "white" }
const rejectStyle = { borderColor: theme.palette.error.main }

const FormUploadFile = ({
  handleChange,
  acceptedFileFormat = {},
  maxFileSize,
  compressToSize
}: IFormUploadFileProps) => {

  const [previewFiles, setPreviewFiles] = useState<File[]>([])
  const [wrongFileFormatWarning, setWrongFileFormatWarning] = useState(false)
  const [fileSizeWarning, setFileSizeWarning] = useState(false)
  const { t } = useTranslation()

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject }: DropzoneState =
    useDropzone({
      multiple: true,
      maxSize: maxFileSize,
      onDrop: async (acceptedFiles: DropzoneFile[], fileRejections) => {
        if (fileRejections.length > 0) {
          const sizeError = fileRejections.some(r =>
            r.errors.some(e => e.code === "file-too-large")
          )
          const typeError = fileRejections.some(r =>
            r.errors.some(e => e.code === "file-invalid-type")
          )

          if (sizeError) {
            setFileSizeWarning(true)
            setWrongFileFormatWarning(false)
          } else if (typeError) {
            setWrongFileFormatWarning(true)
            setFileSizeWarning(false)
          }
          return
        }

        setWrongFileFormatWarning(false)
        setFileSizeWarning(false)

        const processedFiles: DropzoneFile[] = []

        for (const file of acceptedFiles) {
          let fixedFile = file;

          if (file.name.toLowerCase().endsWith(".jfif")) {
            const renamedFile = new File([file], file.name.replace(/\.jfif$/i, ".jpg"), {
              type: "image/jpeg",
              lastModified: file.lastModified,
            });
            fixedFile = Object.assign(renamedFile, {
              path: file.path,
              preview: file.preview,
            });
          }

          if (compressToSize && fixedFile.type.startsWith("image/")) {
            const options = {
              maxSizeMB: compressToSize / (1024 * 1024),
              useWebWorker: true,
            };

            try {
              const compressedBlob = await imageCompression(fixedFile, options);
              const compressedFile = new File([compressedBlob], fixedFile.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });

              processedFiles.push(
                Object.assign(compressedFile, {
                  path: fixedFile.path,
                  preview: fixedFile.preview,
                })
              );
            } catch (err) {
              console.error("Compression failed:", err);
              processedFiles.push(fixedFile);
            }
          } else {
            processedFiles.push(fixedFile);
          }
        }


        setPreviewFiles(processedFiles)
      },
      accept: Object.keys(acceptedFileFormat).length > 0 ? acceptedFileFormat : undefined
    })

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {})
    }),
    [isDragActive, isDragReject, isDragAccept]
  )

  const formatAcceptedFileFormat = (formats: { [mimeType: string]: string[] }): string => {
    return Object.values(formats).flat().join(", ")
  }

  const acceptedFileFormatString = formatAcceptedFileFormat(acceptedFileFormat)

  useEffect(() => {
    if (previewFiles && previewFiles.length > 0) {
      handleChange && handleChange(previewFiles)
    }
  }, [previewFiles])

  return (
    <>
      <Box
        bgcolor={theme.palette.grey[100]}
        border={wrongFileFormatWarning || fileSizeWarning ? "1px solid" : "2px dashed"}
        borderColor={wrongFileFormatWarning || fileSizeWarning ? "error.main" : "secondary.main"}
        borderRadius={2}
        marginBottom={1}
      >
        <Box p={3} {...getRootProps({ style })} flexDirection="column">
          <input {...getInputProps()} />
          <FontAwesomeIcon
            icon={faCloudUpload as IconProp}
            color={theme.palette.secondary.main}
            size="3x"
          />
          <Typography
            color={theme.palette.secondary.main}
            variant="bodyMedium"
            fontWeight="600"
          >
            {t("global.drop_here_or_click_to_select_files", "Drop or click here to select files")}
          </Typography>
          {acceptedFileFormatString && (
            <Typography
              color={theme.palette.secondary.main}
              variant="bodyMedium"
              fontWeight="600"
            >
              {acceptedFileFormatString}
            </Typography>
          )}
        </Box>
      </Box>

      {wrongFileFormatWarning && (
        <Box textAlign="left" sx={{ marginTop: "3px", marginX: "14px" }}>
          <Typography variant="labelSmall" color="error.main">
            {t(
              "global.file_format_is_not_supported",
              "File format is not supported, supported files are:"
            )}{" "}
            {acceptedFileFormatString}
          </Typography>
        </Box>
      )}

      {fileSizeWarning && (
        <Box textAlign="left" sx={{ marginTop: "3px", marginX: "14px" }}>
          <Typography variant="labelSmall" color="error.main">
            {t("global.file_too_large", "File is too large.")}{" "}
            {maxFileSize ? `${(maxFileSize / (1024 * 1024)).toFixed(1)} MB` : ""}
          </Typography>
        </Box>
      )}
    </>
  )
}

export default FormUploadFile
